import "../styles/Dashboard.css";
import { useState, useEffect } from "react";
import api from "../api/api";
import FileUpload from "../components/FileUpload";
import { useAuth } from "../auth/AuthContext";

// Ícones
import { 
  FaFilePdf, FaFileWord, FaFileExcel, FaFileImage, 
  FaFileArchive, FaFileAlt, FaFolder, FaSearch, 
  FaTrash, FaPlus, FaSortAmountDown, FaDownload, 
  FaUserPlus, FaTimes, FaUserShield, FaUser, FaBars 
} from "react-icons/fa";

export default function Dashboard() {
  const { user } = useAuth();

  // --- ESTADOS ---
  const [departments, setDepartments] = useState([]);
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // NOVO: Controle do menu mobile

  const [currentDepartment, setCurrentDepartment] = useState(null);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("name");

  const [newDepartmentName, setNewDepartmentName] = useState("");
  const [newFolderName, setNewFolderName] = useState("");
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareDepartment, setShareDepartment] = useState(null);
  const [shareEmail, setShareEmail] = useState("");

  // --- TRATAMENTO DE ERROS ---
  const handleActionError = (err, customMessage) => {
    console.error(err);
    let detail = "Erro desconhecido.";
    if (err.response) {
      const data = err.response.data;
      detail = data.detail || data.error || (typeof data === 'object' ? Object.values(data)[0] : "Erro no servidor");
    } else if (err.request) {
      detail = "O servidor não respondeu.";
    } else {
      detail = err.message;
    }
    alert(`${customMessage}\nMotivo: ${detail}`);
  };

  // --- CARREGAMENTO DE DADOS ---
  const loadData = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [depRes, folderRes, fileRes] = await Promise.all([
        api.get("departments/"),
        api.get("folders/"),
        api.get("files/"),
      ]);
      setDepartments(depRes.data);
      setFolders(folderRes.data);
      setFiles(fileRes.data);
    } catch (err) {
      handleActionError(err, "Falha ao sincronizar dados.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const selectedDepData = departments.find(d => d.id === currentDepartment);
  const isOwner = (dep) => user && String(dep?.owner_username || dep?.owner).trim() === String(user).trim();

  // --- FUNÇÕES DE CRIAÇÃO/AÇÃO ---
  const handleCreateDepartment = async () => {
    if(!newDepartmentName) return;
    try {
      await api.post("departments/", { name: newDepartmentName });
      setNewDepartmentName("");
      loadData(true);
    } catch (err) { handleActionError(err, "Erro ao criar Departamento."); }
  };

  const handleCreateFolder = async () => {
    if(!newFolderName) return;
    try {
      await api.post("folders/", { name: newFolderName, parent: currentFolder, department: currentDepartment });
      setNewFolderName(""); 
      loadData(true);
    } catch (err) { handleActionError(err, "Erro ao criar pasta."); }
  };

  const handleDeleteFile = async (id, name) => {
    if (!window.confirm(`Apagar ficheiro "${name}"?`)) return;
    try { await api.delete(`files/${id}/`); loadData(true); } 
    catch (err) { handleActionError(err, `Erro ao remover ficheiro.`); }
  };

  const handleDeleteFolder = async (id, name) => {
    if (!window.confirm(`Apagar pasta "${name}" e tudo nela?`)) return;
    try { 
      await api.delete(`folders/${id}/`); 
      if (currentFolder === id) setCurrentFolder(null);
      loadData(true); 
    } catch (err) { handleActionError(err, `Erro ao remover pasta.`); }
  };

  const handleDeleteDepartment = async (e, id, name) => {
    e.stopPropagation();
    if (!window.confirm(`ATENÇÃO: Apagar o Departamento "${name}"?`)) return;
    try { 
      await api.delete(`departments/${id}/`); 
      if (currentDepartment === id) { setCurrentDepartment(null); setCurrentFolder(null); }
      loadData(true); 
    } catch (err) { handleActionError(err, `Erro ao apagar Departamento.`); }
  };

  const handleDownload = async (fileId, fileName) => {
    try {
      const response = await api.get(`files/${fileId}/download/`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) { handleActionError(err, "Falha no download."); }
  };

  const handleShare = async () => {
    try {
      await api.post(`departments/${shareDepartment.id}/share/`, { user_email: shareEmail });
      setShareModalOpen(false); setShareEmail(""); alert("Acesso concedido!");
    } catch (err) { handleActionError(err, "Erro na partilha."); }
  };

  const getFileUI = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    const configs = {
      pdf: { icon: <FaFilePdf />, color: "#E74C3C" },
      doc: { icon: <FaFileWord />, color: "#2B579A" },
      docx: { icon: <FaFileWord />, color: "#2B579A" },
      xls: { icon: <FaFileExcel />, color: "#217346" },
      xlsx: { icon: <FaFileExcel />, color: "#217346" },
      png: { icon: <FaFileImage />, color: "#9B59B6" },
      jpg: { icon: <FaFileImage />, color: "#9B59B6" },
      jpeg: { icon: <FaFileImage />, color: "#9B59B6" },
      zip: { icon: <FaFileArchive />, color: "#F39C12" },
      rar: { icon: <FaFileArchive />, color: "#F39C12" },
    };
    return configs[ext] || { icon: <FaFileAlt />, color: "#7F8C8D" };
  };

  const filterAndSort = (items) => {
    return items
      .filter(item => item.name.toLowerCase().includes(searchTerm.toLowerCase()))
      .sort((a, b) => sortBy === "name" ? a.name.localeCompare(b.name) : b.id - a.id);
  };

  const visibleFolders = filterAndSort(folders.filter(f => f.parent === currentFolder && f.department === currentDepartment));
  const visibleFiles = filterAndSort(files.filter(f => f.folder === currentFolder));

  return (
    <div className="dashboard">
      {loading && <div className="loading-overlay"><div className="spinner"></div></div>}

      {/* Overlay para fechar sidebar no mobile */}
      {isSidebarOpen && <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)}></div>}

      <div className="dashboard-container">
        {/* SIDEBAR */}
        <aside className={`dashboard-sidebar ${isSidebarOpen ? 'open' : ''}`}>
          <div className="sidebar-header">
            <h2 className="brand">SGA NWAYAMI</h2>
            <button className="mobile-close" onClick={() => setIsSidebarOpen(false)}><FaTimes /></button>
          </div>
          
          <div className="create-section">
            <input value={newDepartmentName} onChange={e => setNewDepartmentName(e.target.value)} placeholder="Novo Departamento..." />
            <button className="btn-add" onClick={handleCreateDepartment}><FaPlus /></button>
          </div>

          <nav className="dept-list">
            {departments.map(dep => {
              const owned = isOwner(dep);
              return (
                <div key={dep.id} className={`dept-item ${currentDepartment === dep.id ? 'active' : ''}`} 
                     onClick={() => { setCurrentDepartment(dep.id); setCurrentFolder(null); setIsSidebarOpen(false); }}>
                  <div className="dept-info">
                    <FaFolder className="icon-dept" />
                    <div className="dept-text">
                      <span className="dept-name">{dep.name}</span>
                      <span className="dept-owner-tag">{owned ? `Meu` : `De: ${dep.owner}`}</span>
                    </div>
                  </div>
                  <div className="dept-actions">
                    {owned && (
                      <>
                        <button className="btn-mini" onClick={(e) => { e.stopPropagation(); setShareDepartment(dep); setShareModalOpen(true); }}><FaUserPlus /></button>
                        <button className="btn-mini btn-del" onClick={(e) => handleDeleteDepartment(e, dep.id, dep.name)}><FaTimes /></button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </nav>
        </aside>

        {/* ÁREA PRINCIPAL */}
        <main className="dashboard-main">
          {/* Top Header Mobile */}
          <div className="mobile-top-bar">
             <button className="hamburger" onClick={() => setIsSidebarOpen(true)}><FaBars /></button>
             <span className="mobile-logo">SGA</span>
          </div>

          {currentDepartment ? (
            <>
              <div className="top-toolbar">
                <div className="header-info">
                  <div className="path">
                    <strong>{selectedDepData?.name}</strong>
                    {currentFolder && ` > ${folders.find(f => f.id === currentFolder)?.name}`}
                  </div>
                  <div className="owner-status">
                    {isOwner(selectedDepData) ? (
                      <span className="badge owner"><FaUserShield /> {user}</span>
                    ) : (
                      <span className="badge guest"><FaUser /> {selectedDepData?.owner}</span>
                    )}
                  </div>
                </div>
                
                <div className="toolbar-actions">
                  <div className="sort-box">
                    <FaSortAmountDown />
                    <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                      <option value="name">Nome (A-Z)</option>
                      <option value="date">Recentes</option>
                    </select>
                  </div>
                  <div className="search-bar">
                    <FaSearch />
                    <input type="text" placeholder="Pesquisar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                  </div>
                </div>
              </div>

              {isOwner(selectedDepData) && (
                <div className="control-panel">
                  <input value={newFolderName} onChange={e => setNewFolderName(e.target.value)} placeholder="Nova pasta..." className="input-folder" />
                  <button className="btn-create" onClick={handleCreateFolder}>Criar Pasta</button>
                </div>
              )}
              
              {currentFolder && isOwner(selectedDepData) && (
                <div className="upload-section">
                  <FileUpload folderId={currentFolder} onUpload={() => loadData(true)} />
                </div>
              )}

              {currentFolder && <button className="btn-back" onClick={() => setCurrentFolder(null)}>⬅ Voltar</button>}

              <div className="content-grid">
                {visibleFolders.map(f => (
                  <div key={f.id} className="grid-item folder">
                    <div className="click-area" onClick={() => setCurrentFolder(f.id)}>
                      <FaFolder className="main-icon folder-icon" />
                      <span className="item-name">{f.name}</span>
                    </div>
                    {isOwner(selectedDepData) && (
                      <button className="del-btn-grid" onClick={() => handleDeleteFolder(f.id, f.name)}><FaTrash /></button>
                    )}
                  </div>
                ))}

                {visibleFiles.map(file => {
                  const ui = getFileUI(file.name);
                  const isImage = ['png', 'jpg', 'jpeg', 'webp'].includes(file.name.split('.').pop().toLowerCase());
                  return (
                    <div key={file.id} className="grid-item file">
                      <div className="item-actions-top">
                        <button className="mini-dl" onClick={() => handleDownload(file.id, file.name)}><FaDownload /></button>
                        {isOwner(selectedDepData) && (
                          <button className="mini-del-file" onClick={() => handleDeleteFile(file.id, file.name)}><FaTrash /></button>
                        )}
                      </div>
                      <div className="item-preview" onClick={() => handleDownload(file.id, file.name)}>
                        {isImage ? <img src={file.file} alt="" className="thumb" /> : <span className="main-icon" style={{ color: ui.color }}>{ui.icon}</span>}
                      </div>
                      <span className="item-name" title={file.name}>{file.name}</span>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="empty-state">
              <FaFolder size={80} color="#cbd5e1" />
              <h3>Selecione um Departamento</h3>
            </div>
          )}
        </main>
      </div>

      {/* MODAL DE SHARE */}
      {shareModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>👥 Partilhar {shareDepartment?.name}</h3>
            <input type="email" placeholder="Email..." value={shareEmail} onChange={e => setShareEmail(e.target.value)} className="share-input" />
            <div className="modal-btns">
              <button className="btn-save" onClick={handleShare}>Confirmar</button>
              <button className="btn-cancel" onClick={() => setShareModalOpen(false)}>Sair</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}